import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { calendarioService } from "../services/calendario-service";
import { CalendarEvent, NewEventPayload, UpdateEventPayload } from "../types";

export function useUserProfile() {
  return useQuery({
    queryKey: ["user-profile"],
    queryFn: () => calendarioService.getUserProfile(),
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

const QUERY_KEY = "calendar-events";

export function useCalendarEvents(startDate: Date, endDate: Date) {
  return useQuery<CalendarEvent[]>({
    queryKey: [QUERY_KEY, startDate.toISOString(), endDate.toISOString()],
    queryFn: () => calendarioService.getCalendarEvents(startDate, endDate),
    refetchOnWindowFocus: false,
    staleTime: 2 * 60 * 1000, // 2 min
    retry: 1,
  });
}

export function useCreateEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (event: NewEventPayload) =>
      calendarioService.createEvent(event),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useUpdateEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      eventId,
      event,
    }: {
      eventId: string;
      event: UpdateEventPayload;
    }) => calendarioService.updateEvent(eventId, event),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useDeleteEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (eventId: string) => calendarioService.deleteEvent(eventId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useAcceptEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ eventId, comment }: { eventId: string; comment?: string }) =>
      calendarioService.acceptEvent(eventId, comment),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useDeclineEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ eventId, comment }: { eventId: string; comment?: string }) =>
      calendarioService.declineEvent(eventId, comment),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useTentativelyAcceptEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ eventId, comment }: { eventId: string; comment?: string }) =>
      calendarioService.tentativelyAcceptEvent(eventId, comment),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useCancelEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ eventId, comment }: { eventId: string; comment?: string }) =>
      calendarioService.cancelEvent(eventId, comment),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useSearchPeople(query: string) {
  return useQuery({
    queryKey: ["people-search", query],
    queryFn: () => calendarioService.searchPeople(query),
    enabled: query.length >= 2,
    refetchOnWindowFocus: false,
    staleTime: 30 * 1000,
  });
}
